import { useCallback, useEffect, useState } from 'react';
import { PauseCircle, PlayCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useStoreStatus } from '../../context/StoreStatusContext';
import ConfirmDialog from '../ConfirmDialog';

// The pause switch for staff. While paused, the public sees the "we're updating
// our inventory" page and checkout refuses orders (server side too). Reopening
// emails, once, everyone who left an address during THIS pause.
//
// Sits at the top of every admin screen so it is one tap from anywhere, on the
// phone too.

// mm.dd.yyyy h:mm AM in Pacific, the shop's clock.
function pacific(iso) {
  if (!iso) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.month}.${p.day}.${p.year} ${p.hour}:${p.minute} ${p.dayPeriod}`;
}

export default function StoreSwitch() {
  const { paused, pausedAt, loaded, setStatus, refresh } = useStoreStatus();
  const [confirm, setConfirm] = useState(null); // 'pause' | 'reopen' | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [waiting, setWaiting] = useState(null);

  // How many people have asked to be emailed during the current pause.
  const loadWaiting = useCallback(async () => {
    const { data: open } = await supabase
      .from('sv_store_pauses').select('id').is('resumed_at', null).maybeSingle();
    if (!open?.id) { setWaiting(null); return; }
    const { count } = await supabase
      .from('sv_restock_signups').select('id', { count: 'exact', head: true }).eq('pause_id', open.id);
    setWaiting(count ?? 0);
  }, []);

  useEffect(() => {
    if (!paused) { setWaiting(null); return undefined; }
    loadWaiting();
    const t = setInterval(loadWaiting, 30000);
    return () => clearInterval(t);
  }, [paused, loadWaiting]);

  const apply = async () => {
    const goingPaused = confirm === 'pause';
    setBusy(true);
    setError('');
    setNote('');
    try {
      const { data, error: err } = await supabase.rpc('sv_set_store_paused', { p_paused: goingPaused });
      if (err) throw err;
      const row = Array.isArray(data) ? data[0] : data;
      setStatus({ paused: !!row?.paused, pausedAt: row?.paused_at || null, loaded: true });

      if (!goingPaused) {
        // Email everyone who signed up during the pause. If this call fails the
        // store is still open, and a 5-minute background job sends the same
        // emails anyway, so a failure here is a delay, not a loss.
        const { data: res, error: mailErr } = await supabase.functions.invoke('shinyvault-store-notify', { body: {} });
        if (mailErr || res?.error) {
          setNote('Store reopened. The "we\'re back" emails will go out within a few minutes.');
        } else {
          setNote(res?.sent
            ? `Store reopened. Emailed ${res.sent} ${res.sent === 1 ? 'person who was' : 'people who were'} waiting.`
            : 'Store reopened. Nobody was waiting on an email.');
        }
      }
    } catch (e) {
      setError(e?.message || 'Could not change the store status.');
      await refresh();
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  if (!loaded) return null;

  const dot = paused ? 'var(--warning)' : 'var(--success, #1f9d55)';

  return (
    <>
      <div
        className="sv-card"
        style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: 20,
          border: paused ? '1px solid var(--warning)' : '1px solid var(--border)',
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: 999, background: dot, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem' }}>
            {paused ? 'Store is paused' : 'Store is open'}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {paused
              ? `Since ${pacific(pausedAt)} Pacific${waiting != null ? ` · ${waiting} waiting for an email` : ''}`
              : 'Customers can shop and check out.'}
          </div>
        </div>
        <button
          onClick={() => setConfirm(paused ? 'reopen' : 'pause')}
          disabled={busy}
          className={paused ? 'sv-btn' : 'sv-btn-outline'}
          style={{ padding: '8px 14px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 7 }}
        >
          {paused ? <PlayCircle size={16} /> : <PauseCircle size={16} />}
          {paused ? 'Reopen store' : 'Pause store'}
        </button>
      </div>

      {note && (
        <div style={{ fontSize: '0.85rem', marginBottom: 16, color: 'var(--text-muted)' }}>{note}</div>
      )}
      {error && (
        <div style={{ fontSize: '0.85rem', marginBottom: 16, color: 'var(--danger)' }}>{error}</div>
      )}

      {confirm === 'pause' && (
        <ConfirmDialog
          title="Pause the store?"
          message={'Visitors will see "We\'re updating our inventory" with a box to leave their email, and nobody can check out until you reopen. Staff still see the full store.'}
          confirmLabel="Pause store"
          busyLabel="Pausing…"
          danger={false}
          busy={busy}
          onConfirm={apply}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'reopen' && (
        <ConfirmDialog
          title="Reopen the store?"
          message={`Shopping and checkout turn back on right away${waiting ? `, and the ${waiting} ${waiting === 1 ? 'person' : 'people'} who asked will get one "we're back" email` : ''}.`}
          confirmLabel="Reopen store"
          busyLabel="Reopening…"
          danger={false}
          busy={busy}
          onConfirm={apply}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}
